import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

interface PrivateRouteProps {
  children: React.ReactNode;
  roles?: string[];
  permission?: string;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, roles, permission }) => {
  const { user, loading } = useAuth();
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // A contractor has no place in the system pages: only its portal
  if (user.role === 'contractor') {
    return <Navigate to="/contractor" replace />;
  }

  if (roles && roles.length > 0) {
    let userRole = user.role?.toLowerCase() || '';
    if (!userRole) {
      if (user.is_admin) userRole = 'admin';
      else if (user.is_editor) userRole = 'editor';
      else if (user.is_client) userRole = 'client';
    }

    const allowedRoles = roles.map(r => r.toLowerCase());
    if (!allowedRoles.includes(userRole)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Check module permission. Admins bypass module flags entirely (house
  // rule: the admin role is unconditional) — this also protects against a
  // stale cached user object missing a recently-added flag.
  if (permission) {
    let hasPermission =
      user.role?.toLowerCase() === 'admin' ||
      user[permission as keyof typeof user] === true;

    // For client users, also check client_profile_info representative permissions
    if (user.role === 'client' && user.client_profile_info) {
      const cpi = user.client_profile_info;
      const permMap: Record<string, boolean> = {
        can_access_payments: cpi.can_access_payments,
        can_access_proposals: cpi.can_access_proposals,
        can_access_documents: cpi.can_access_documents,
      };
      if (permission in permMap) {
        hasPermission = permMap[permission];
      }
    }

    if (!hasPermission) {
      // Show permission denied page instead of silent redirect
      return (
        <div className="d-flex justify-content-center align-items-center vh-100">
          <div className="text-center">
            <div className="mb-3">
              <i className="bi bi-shield-lock text-danger" style={{ fontSize: '4rem' }}></i>
            </div>
            <h4 className="text-danger mb-2">
              {isRTL ? 'غير مصرح بالوصول' : 'Access Denied'}
            </h4>
            <p className="text-muted mb-4">
              {isRTL
                ? 'ليس لديك صلاحية للوصول إلى هذه الصفحة. يرجى التواصل مع المسؤول لطلب الصلاحيات اللازمة.'
                : 'You do not have permission to access this page. Please contact your administrator to request access.'}
            </p>
            <a href="/dashboard" className="btn btn-primary">
              <i className="bi bi-house me-1"></i>
              {isRTL ? 'العودة للوحة التحكم' : 'Back to Dashboard'}
            </a>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

export default PrivateRoute;
